import React from "react";
import { Link } from "react-router-dom";
import { Content } from "../../components/Layout";
import { ActionText, Box, Flex } from "../../components/Flex";
import { Scrollbar } from "../../components/Scrollbar";
import { Table } from "../../components/Table";
import { Label, Paragraph, Title } from "../../components/Text";
import { EditDetailsIcon } from "../../icons/EditDetailsIcon";
import { EditIcon } from "../../icons/EditIcon";
import { PlusIcon } from "../../icons/PlusIcon";
import { formatDate } from "../../utils";
import { RemoveIcon } from "../../icons/RemoveIcon";
import { CircleIcon } from "../../icons/CircleIcon";
import { Badge } from "../../components/Circle";
import { Button } from "../../components/Button";


const OrgDetails = ({
    data,
    showForm,
    loading,
    deleteContract,
    isAdmin,
    licenseRead,
    contractsWrite,
    userWrite,
    orgDelete,
    deleteItem,
    pathname
}) => {

    const fields = [
        { label: "organization name", attr: "name" },
        { label: "organization alias", attr: "alias" },
        { label: "phone number", attr: "phone" },
        { label: "contact email", attr: "email", render: d => <a href={`mailto: ${d}`}>{d}</a> },
        { label: "address", attr: "addr" },
        { label: "notes", attr: "notes" }
    ];

    let columns = [
        {
            title: <CircleIcon size={16} />,
            dataIndex: 'expired',
            width: 50,
            render: (text, record, index) => <Badge color={ text < 0 ? "var(--sp-error-color)" : text == 0 ? "var(--sp-warning-color)" : "var(--sp-success-color)"} />,
            sorter: (a, b) => { if (a && a.hasOwnProperty("expired")) return a.expired - b.expired}
        },
        {
            title: "Name",
            dataIndex: 'contractName',
            render: (text, record, index) => <Paragraph nowrap title={text}>{text}</Paragraph>,
            sorter: (a, b) => { if (a && a.hasOwnProperty("contractName") && a.contractName) return a.contractName.localeCompare(b.contractName) }
        },

        {
            title: "Expiration date",
            dataIndex: "endDate",
            width: 120,
            defaultSortOrder: "descend",
            render: (text, record, index) => (formatDate(text, "DD-MMM-YYYY")),
            sorter: (a, b) => { if (a && a.hasOwnProperty("endDate") && a.endDate) return new Date(a.endDate) - new Date(b.endDate) },
        }];

    if (contractsWrite) {
        columns = columns.concat([{
            title: "Ecokits",
            dataIndex: "ecokits",
            width: 70,
            render: (text, record, index) => text == 1 ? "Yes" : text == 0 ? "No" : "-",
            sorter: (a, b) => { if (a && a.hasOwnProperty("ecokits") && a.ecokits) return a.ecokits - b.ecokits },
        },
        {
            title: "Minor Rel",
            dataIndex: "rToMinorRel",
            width: 90,
            render: (text, record, index) => text == 1 ? "Yes" : text == 0 ? "No" : "-",
            sorter: (a, b) => { if (a && a.hasOwnProperty("rToMinorRel") && a.rToMinorRel) return a.rToMinorRel - b.rToMinorRel },
        },
        {
            dataIndex: "edit",
            width: 50,
            render: (text, record, index) => (
                <EditIcon
                    size={20}
                    color="var(--sp-label-color)"
                    hoverColor="var(--sp-paragraph-color)"
                    onClick={() => showForm("contracts", { orgAlias: data.alias, ...record })}
                />
            )
        },
        {
            dataIndex: "remove",
            width: 50,
            render: (text, record, index) => (
                <RemoveIcon
                    size={20}
                    color="var(--sp-label-color)"
                    hoverColor="var(--sp-paragraph-color)"
                    onClick={() => deleteContract({ alias: data.alias, contract: record.contractName, org: data.name })}
                />
            )
        }])
    };

    return(
        <Content>
            <Title h3 isLoading={loading}>{data && data.hasOwnProperty("name") && data.name}</Title>
            <Flex height={`calc(100% - ${orgDelete ? 96 : 44}px)`} gap={{ right: 32 }}>
                <Box flex={1} gap={{ bottom: 17 }}>
                    <Flex justify="space-between" align="center">
                        <Title h4 capitalize>about organization</Title>
                        {isAdmin &&
                        <ActionText onClick={() => showForm("organisations", data)}>
                            <span><EditDetailsIcon /></span>
                            <span>Edit Info</span>
                        </ActionText>}
                    </Flex>
                    <Scrollbar>
                        <Flex direction="column" gap={{ bottom: 20 }}>
                            {fields.map((field, idx) => (
                                <Flex key={`field-${idx}`} direction="column">
                                    <Label>{field.label}</Label>
                                    <Paragraph isLoading={loading}>
                                        {data && data.hasOwnProperty(field.attr) && data[field.attr]
                                            ? field.render
                                                ? field.render(data[field.attr])
                                                : data[field.attr]
                                            : "-"
                                        }
                                    </Paragraph>
                                </Flex>
                            ))}
                        </Flex>
                    </Scrollbar>
                </Box>
                <Flex flex={2} direction="column" gap={{ bottom: 12 }}>
                    <Box style={{ minHeight: "156px" }}>
                        <Flex justify="space-between">
                            <Title h4 capitalize>contracts</Title>
                            {contractsWrite &&
                            <ActionText onClick={() => showForm("contracts")} title="Add Contract">
                                <PlusIcon />
                                {/* <span>Add Contract</span> */}
                            </ActionText>}
                        </Flex>
                        <div style={{ height: "calc(100% - 28px)" }}>
                            <Table
                                columns={columns}
                                dataSource={data && data.hasOwnProperty("contracts") ? data.contracts : []}
                                showSorterTooltip={false}
                                emptyText={{text: "no contracts available", size: "18px"}}
                                loading={loading}
                            />
                        </div>
                    </Box>
                    <Flex gap={{ right: 12 }}>
                        <Box flex={1}>
                            <Flex justify="space-between" align="center">
                                <Title h4 capitalize>
                                    <Link to={{pathname: `${pathname}/users`, state: { from: null }}}>
                                        users
                                    </Link>
                                </Title>
                                {userWrite &&
                                <ActionText onClick={() => showForm("users")} title="Add User">
                                    <span><PlusIcon /></span>
                                    {/* <span>Add User</span> */}
                                </ActionText>}
                            </Flex>
                        </Box>
                        {isAdmin &&
                        <Box flex={1}>
                            <Title h4 capitalize>
                                <Link to={{pathname: `${pathname}/issues`, state: { from: null }}}>
                                    issues
                                </Link>
                            </Title>
                        </Box>}
                        {licenseRead &&
                        <Box flex={1}>
                            <Flex justify="space-between" align="center" gap={{ right: 8 }}>
                                <Title h4 capitalize>
                                    <Link to={{pathname: `${pathname}/licenses`, state: { from: null }}}>
                                        licenses
                                    </Link>
                                </Title>
                                {isAdmin &&
                                <ActionText onClick={() => showForm("licenses")} title="Add License">
                                    <span><PlusIcon /></span>
                                    {/* <span>Add License</span> */}
                                </ActionText>}
                            </Flex>
                        </Box>}
                    </Flex>
                </Flex>
            </Flex>
            {orgDelete
                ? <Flex>
                    <Button
                        danger
                        disabled={loading || !data}
                        onClick={() => deleteItem({
                            content: <Paragraph>Are you sure you want to delete the <b>{data.name}</b> organization without the possibility of recovery?</Paragraph>,
                            id: data.alias
                        })}
                    >
                        <RemoveIcon size={20} />
                        Delete organization
                    </Button>
                </Flex>
                : null}
        </Content>
    );
};

export { OrgDetails }